import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Import the gradient colors from the core loading module
import {
  gradientColors,
  ThemeColor,
} from "../../../core/loading/gradient-colors";

interface SequentialLoadingStepsProps {
  steps: string[];
  onLastStepNearCompletion?: () => void;
  themeColor?: ThemeColor;
  duration?: number; // Total duration in seconds for all steps
}

export function SequentialLoadingSteps({
  steps,
  onLastStepNearCompletion,
  themeColor = "blue",
  duration = 32, // Default to 32 seconds (8 seconds per step for 4 steps)
}: SequentialLoadingStepsProps) {
  const { t } = useTranslation();
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const selectedGradient = gradientColors[themeColor] || gradientColors["blue"];

  // Calculate step duration based on total duration and number of steps
  const stepDuration = steps.length > 0 ? duration / steps.length : 8;

  // Reset when component mounts
  useEffect(() => {
    setCompletedSteps([]);
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  // Call onLastStepNearCompletion when the last step is near completion
  useEffect(() => {
    if (steps.length > 0) {
      const timer = setTimeout(
        () => {
          if (onLastStepNearCompletion) {
            onLastStepNearCompletion();
          }
        },
        (duration - 2) * 1000, // 2 seconds before the total duration completes
      );

      return () => clearTimeout(timer);
    }
  }, [steps.length, onLastStepNearCompletion, duration]);

  // Progress through steps
  useEffect(() => {
    if (steps.length === 0) return;

    const timers: NodeJS.Timeout[] = [];

    // Set up timers for each step completion
    steps.forEach((_, index) => {
      const timer = setTimeout(
        () => {
          setCompletedSteps((prev) => {
            if (!prev.includes(index)) {
              return [...prev, index];
            }
            return prev;
          });

          if (index < steps.length - 1) {
            setCurrentStep(index + 1);
          }
        },
        (index + 1) * stepDuration * 1000,
      );

      timers.push(timer);
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [steps, stepDuration]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-8 py-4"
      data-testid="sequential-loading-steps"
    >
      {steps.map((step, index) => {
        const isCompleted =
          completedSteps.includes(index) && index !== steps.length - 1;
        const isStepActive = index === currentStep && !isCompleted;

        // Use theme color for active and completed steps
        const activeColor =
          selectedGradient.textColor || "text-rose-600 dark:text-rose-400";
        const inactiveColor = "text-gray-400 dark:text-gray-500";

        // Border and background colors based on theme
        const activeBorder = `border-2 ${selectedGradient.border.replace("bg-", "border-")}`;
        const completedBg = selectedGradient.border; //"bg-green-600 dark:bg-green-400";
        const inactiveBorder = "border-2 border-gray-300 dark:border-gray-600";

        return (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`flex items-center space-x-4 ${
              isStepActive
                ? activeColor
                : isCompleted
                  ? activeColor
                  : inactiveColor
            }`}
          >
            <div
              className={`relative w-10 h-10 rounded-full flex items-center justify-center ${
                isStepActive
                  ? activeBorder
                  : isCompleted
                    ? completedBg
                    : inactiveBorder
              }`}
            >
              {isStepActive && (
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [1, 0.5, 1],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className={`absolute inset-0 rounded-full border-2 ${selectedGradient.border.replace("bg-", "border-")}/50`}
                />
              )}
              {isStepActive ? (
                <div
                  className={`w-3 h-3 ${selectedGradient.border.replace("bg-gradient-to-r", "bg")} rounded-full`}
                >
                  <div className="w-3 h-3 bg-gray-400 dark:bg-gray-500 rounded-full" />
                </div>
              ) : isCompleted ? (
                <CheckCircle2 className="w-5 h-5 text-white" />
              ) : (
                <div className="w-3 h-3 bg-gray-400 dark:bg-gray-500 rounded-full" />
              )}
            </div>
            <div className="flex-1">
              <p
                className={`text-sm ${isStepActive || isCompleted ? "font-medium" : "font-normal"}`}
              >
                {t(step)}
              </p>
              <div
                className={`mt-1 h-1 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 ${
                  isStepActive || isCompleted ? "opacity-100" : "opacity-0"
                }`}
              >
                <motion.div
                  className={`h-full ${selectedGradient.border}`}
                  initial={{ width: "0%" }}
                  animate={{
                    width: isCompleted
                      ? "100%"
                      : isStepActive
                        ? index === steps.length - 1
                          ? "98%"
                          : "100%"
                        : "0%",
                  }}
                  transition={{
                    duration: isStepActive ? stepDuration : 0.5,
                    ease:
                      index === steps.length - 1
                        ? [0.87, 0, 0.13, 1]
                        : "linear",
                  }}
                />
              </div>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
